/**
 * The company ride goal (Mission 2026: 18M rides) as plain functions: pace, milestones, and
 * which market's new rides the next live toast shows.
 */

/** Share of the calendar year elapsed at `now`, between 0 and 1. */
export function yearElapsed(year: number, now: Date): number {
  const start = new Date(year, 0, 1).getTime();
  const end = new Date(year + 1, 0, 1).getTime();
  return Math.min(1, Math.max(0, (now.getTime() - start) / (end - start)));
}

/** Where the goal should be by now, and how far ahead (+) or behind (−) the total is. */
export function paceOf(total: number, target: number, year: number, now: Date): { expected: number; delta: number } {
  const expected = Math.round(target * yearElapsed(year, now));
  return { expected, delta: total - expected };
}

/** Milestones: every million rides and every tenth of the target. */
export function milestonesBetween(before: number, after: number, target: number): number[] {
  if (after <= before) return [];
  const steps = new Set<number>();
  const add = (step: number) => {
    if (step <= 0) return;
    for (let m = Math.floor(before / step) * step + step; m <= after; m += step) steps.add(m);
  };
  add(1_000_000);
  add(Math.round(target / 10));
  return [...steps].sort((a, b) => a - b);
}

/**
 * Each market's new rides between two polls: the rise of its today count. A drop (a recount, or
 * a new day starting) is not a ride, and neither is a market that only just appeared with its
 * whole day's count.
 */
export function todayDeltas(before: ReadonlyMap<string, number>, now: ReadonlyMap<string, number>): [string, number][] {
  const out: [string, number][] = [];
  for (const [name, count] of now) {
    const prev = before.get(name);
    if (prev !== undefined && count > prev) out.push([name, count - prev]);
  }
  return out;
}

/** New rides of one market waiting for their toast. */
export interface Pending {
  count: number;
  /** When this market last had a toast (0: never). */
  shownAt: number;
}

/**
 * The market whose new rides the next toast shows: among markets with new rides, the one shown
 * longest ago (never shown first), more new rides breaking ties. Null when nothing is new.
 */
export function nextToastMarket(pending: ReadonlyMap<string, Pending>): string | null {
  let best: [string, Pending] | null = null;
  for (const entry of pending) {
    const [, p] = entry;
    if (p.count <= 0) continue;
    if (!best || p.shownAt < best[1].shownAt || (p.shownAt === best[1].shownAt && p.count > best[1].count)) best = entry;
  }
  return best ? best[0] : null;
}

/** 20–40 s between toasts, so markets appear to take turns rather than flood the screen. */
export const TOAST_MIN_GAP_MS = 20_000;
export const TOAST_JITTER_MS = 20_000;
export const TOAST_VISIBLE_MS = 4_000;
export const nextToastDelay = (random = Math.random) => TOAST_MIN_GAP_MS + Math.round(random() * TOAST_JITTER_MS);

/** "7.2M", "850K" in the admin's language. */
export function compact(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: value >= 1_000_000 ? 2 : 1 }).format(value);
}

/** Progress as a percentage with one decimal ("40.3"), never above 100. */
export function percent(total: number, target: number): number {
  if (target <= 0) return 0;
  return Math.min(100, Math.floor((total / target) * 1000) / 10);
}
